import NameLinkCell from '@/components/NameLinkCell';
import { sampleQueryKeys } from '@/config/queryKeys';
import { SAMPLES_ALLOWED_ROLES } from '@/config/samples';
import { defaultColDef, mainIconSize } from '@/constants';
import { useDateFormatter, useRouteProtection } from '@/hooks';
import { createAgGridDatasource } from '@/lib/createAgGridDatasource';
import { type PaginatedSamplesResponse, type SampleWithNamesPublic } from '@/types';
import { applyStoredColumnVisibility } from '@/utils/applyStoredColumnVisibility';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import type { ColDef, GridApi, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import { CloudDownload } from 'lucide-react';
import React, { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyles from '../../styles/index.module.css';

import InstrumentRunCellRenderer from './components/InstrumentRunCellRenderer';
import QcStatusCell from './components/QcStatusCell';
import SamplesStatsBar from './components/SamplesStatsBar';
import StudyCellRenderer from './components/StudyCellRenderer';
import { PAGE_SIZE } from './constants/PAGE_SIZE';
import { useOrgId } from './helpers';
import styles from './styles.module.css';

export const SamplesPage: FC = () => {
  useRouteProtection([...SAMPLES_ALLOWED_ROLES]);
  const { t } = useTranslation('studies');
  const orgId = useOrgId();
  const gridRef = useRef<AgGridReact<SampleWithNamesPublic> | null>(null);
  const gridApiRef = useRef<GridApi<SampleWithNamesPublic> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formatDate = useDateFormatter();
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const colDefs = useMemo<ColDef<SampleWithNamesPublic>[]>(
    () => [
      { field: 'well_position', headerName: t('columnHeaders.well'), minWidth: 80 },
      { field: 'demux_index', headerName: t('columnHeaders.demuxId'), minWidth: 90 },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) =>
          params.data ? (
            <NameLinkCell
              ariaLabel={t('goToSample')}
              className={styles.nameCell}
              fullHeight
              id={params.data.id}
              name={params.data.name}
              params={{ sampleId: params.data.id }}
              to="/samples/$sampleId"
            />
          ) : null,
        field: 'name',
        headerName: t('columnHeaders.sample'),
        minWidth: 200,
        width: 300,
      },
      { field: 'sample_group', headerName: t('columnHeaders.sampleGroup'), minWidth: 125 },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QcStatusCell message={params.data?.message} value={params.value as string | null | undefined} />
        ),
        field: 'run_qc',
        headerName: t('columnHeaders.sampleQualityQc'),
        minWidth: 165,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QcStatusCell message={params.data?.message} value={params.value as string | null | undefined} />
        ),
        field: 'decode_qc',
        headerName: t('columnHeaders.decodeQc'),
        minWidth: 100,
      },
      {
        // TODO: switch to field: 'lane_qc' when SampleWithNamesPublic adds lane_qc
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QcStatusCell message={params.data?.message} value={undefined} />
        ),
        colId: 'lane_qc',
        headerName: t('columnHeaders.laneQc'),
        minWidth: 85,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field: 'controls_qc' when SampleWithNamesPublic adds controls_qc
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <QcStatusCell message={params.data?.message} value={undefined} />
        ),
        colId: 'controls_qc',
        headerName: t('columnHeaders.controlsQc'),
        minWidth: 115,
        valueGetter: () => undefined,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => {
          const msg = params.value as string | null | undefined;
          return (
            <Stack alignItems="center" gap={1} orientation="horizontal">
              <Text as="span">{msg ? t('warning_yes') : t('warning_no')}</Text>
              <QcStatusCell message={msg ?? null} value={msg ? 'Warning' : 'Pass'} />
            </Stack>
          );
        },
        colId: 'warning',
        headerName: t('columnHeaders.warning'),
        minWidth: 85,
        valueGetter: (params: ValueGetterParams<SampleWithNamesPublic>) => params.data?.message ?? null,
      },
      { field: 'species', headerName: t('columnHeaders.species'), minWidth: 85 },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <StudyCellRenderer className={styles.nameCell} data={params.data} fullHeight />
        ),
        field: 'study_name',
        headerName: t('columnHeaders.study'),
        width: 300,
      },
      {
        cellRenderer: (params: ICellRendererParams<SampleWithNamesPublic>) => (
          <InstrumentRunCellRenderer className={styles.nameCell} data={params.data} fullHeight />
        ),
        field: 'instrument_run_name',
        headerName: t('columnHeaders.instrumentRun'),
        minWidth: 160,
      },
      { field: 'assay_version', headerName: t('columnHeaders.assay') },
      {
        // TODO: switch to field: 'flow_cell' when SampleWithNamesPublic adds flow_cell
        colId: 'flow_cell',
        headerName: t('columnHeaders.flowcell'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field: 'lane' when SampleWithNamesPublic adds lane
        colId: 'lane',
        headerName: t('columnHeaders.lane'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field: 'library_group' when SampleWithNamesPublic adds library_group
        colId: 'library_group',
        headerName: t('columnHeaders.libraryGroup'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: switch to field when SampleWithNamesPublic adds instrument
        colId: 'instrument',
        headerName: t('columnHeaders.instrument'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        hide: true,
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      },
      {
        field: 'updated_at',
        headerName: t('columnHeaders.updated'),
        hide: true,
        valueFormatter: ({ value }: { value: string | null | undefined }) => (value ? formatDate(value) : ''),
      },
      {
        // TODO: expose created_by on SampleWithNamesPublic when backend supports it
        colId: 'created_by',
        headerName: t('columnHeaders.createdBy'),
        hide: true,
        valueGetter: () => undefined,
      },
      {
        // TODO: expose updated_by on SampleWithNamesPublic when backend supports it
        colId: 'updated_by',
        headerName: t('columnHeaders.updatedBy'),
        hide: true,
        valueGetter: () => undefined,
      },
    ],
    [formatDate, t],
  );

  const datasource = useMemo(
    () =>
      orgId
        ? createAgGridDatasource<SampleWithNamesPublic, PaginatedSamplesResponse>(
            sampleQueryKeys.endpoints.listSearch(orgId),
          )
        : undefined,
    [orgId],
  );

  const handleGridReady = (params: { api: GridApi<SampleWithNamesPublic> }) => {
    gridApiRef.current = params.api;
    applyStoredColumnVisibility(params.api, 'samples-table');
    if (datasource) {
      params.api.setGridOption('serverSideDatasource', datasource);
    }
    sizeColumnsOnFirstVisit(params.api, 'samples-table');
  };

  // When the selected org changes, the datasource (its endpoint includes the orgId) is
  // recreated; re-apply it so the grid refetches for the new org without a remount.
  useEffect(() => {
    if (gridApiRef.current && datasource) {
      gridApiRef.current.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  return (
    <Stack className={styles.container} orientation="vertical">
      <Stack alignItems="center" className={styles.newTitleRow} justifyContent="space-between" orientation="horizontal">
        <Text as="h1" className={styles.title}>
          {t('title')}
        </Text>
        <Stack alignItems="center" gap={2} orientation="horizontal">
          <Tooltip
            content={t('download_list_coming_soon')}
            placement="bottom"
            trigger={
              <Button data-testid="samples-download-list" isDisabled size="md" visual="unstyled">
                <CloudDownload size={mainIconSize} />
                {t('download_list')}
              </Button>
            }
          />
        </Stack>
      </Stack>

      <SamplesStatsBar totalCount={totalCount} />

      <div className={styles.tableContainer}>
        <Table
          columnDefs={colDefs}
          container={{ className: globalStyles.scrollbars, ref: containerRef }}
          defaultColDef={defaultColDef}
          density="normal"
          domLayout="normal"
          enableResponsiveResize={false}
          getRowId={(params: { data: SampleWithNamesPublic }) => params.data.id}
          onGridPreDestroyed={e => flushColumnState(e.api, 'samples-table')}
          onGridReady={handleGridReady}
          onPaginationChanged={e => {
            const rowCount = e.api.paginationGetRowCount();
            if (rowCount >= 0) {
              setTotalCount(rowCount);
            }
          }}
          pagination
          paginationPageSize={PAGE_SIZE}
          ref={gridRef}
          resizeDebounceMs={150}
          rowBuffer={30}
          rowModelType="serverSide"
          tableId="samples-table"
          zebraStriping
        />
      </div>
    </Stack>
  );
};

export default SamplesPage;
